import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ClassyLoading } from './components/ClassyLoading';
import { NewVersionAvailable } from './components/NewVersionAvailable';
import { OnboardingOutlet } from './components/OnboardingOutlet';
import { t } from '@lingui/core/macro';

export function Router() {
  return (
    <Suspense fallback={<ClassyLoading message={t`Redirecting...`} />}>
      <Routes>
        <Route path="/profile" element={<Profile />} />
        <Route path="/p/" element={<OnboardingOutlet />}>
          <Route index element={<HomePage />} />
          <Route path="/p/:programSlug" element={<ProgramBySlug />} />
          <Route path="/p/:programSlug/s/:skillSlug" element={<SkillLesson />} />
        </Route>
        <Route path="/section/login" element={<SectionTokenPage />} />

        <Route path="/demo/p/:programSlug/s/:skillSlug" element={<UserTest />} />
        <Route path="/demo/p/:programSlug/s/:skillSlug/t/:toolName" element={<UserToolTest />} />
        <Route
          path="/demo/p/:programSlug/s/:skillSlug/c/:competencySlug/e/:exerciseSlug"
          element={<UserToolExerciseTest />}
        />
        <Route
          path="/practice/p/:programSlug/s/:skillSlug/c/:competencySlug/e/:exerciseSlug"
          element={<UserToolExerciseTest />}
        />
        <Route path="/reporting/progress" element={<AnalyticsDashboard />} />
        <Route path="/reporting/nps" element={<NpsReport />} />

        <Route
          path="/_sys/*"
          Component={() => {
            setTimeout(() => {
              // localhost dev glitch
              window.location.href = '/_sys/?' + new Date().getTime();
            }, 0);
            return null;
          }}
        />

        <Route path="*" element={<Navigate to="/p" replace />} />
      </Routes>
    </Suspense>
  );
}

// Lazy load all pages
const HomePage = lazy(() =>
  import('./pages/Home').then((m) => ({ default: m.HomePage })).catch(catcher),
);
const Profile = lazy(() =>
  import('./pages/Profile').then((m) => ({ default: m.ProfilePage })).catch(catcher),
);
const ProgramBySlug = lazy(() =>
  import('./pages/ProgramBySlug').then((m) => ({ default: m.ProgramBySlug })).catch(catcher),
);
const SkillLesson = lazy(() =>
  import('./pages/ProgramSkill').then((m) => ({ default: m.ProgramSkill })).catch(catcher),
);
const UserTest = lazy(() =>
  import('./pages/UserTest').then((m) => ({ default: m.UserTest })).catch(catcher),
);
const UserToolTest = lazy(() =>
  import('./pages/UserToolTest').then((m) => ({ default: m.UserToolTest })).catch(catcher),
);
const UserToolExerciseTest = lazy(() =>
  import('./pages/UserToolExerciseTest')
    .then((m) => ({ default: m.UserToolExerciseTest }))
    .catch(catcher),
);
const SectionTokenPage = lazy(() =>
  import('./pages/SectionToken').then((m) => ({ default: m.SectionTokenPage })).catch(catcher),
);

const AnalyticsDashboard = lazy(() =>
  import('./pages/AnalyticsDashboard')
    .then((m) => ({ default: m.AnalyticsDashboard }))
    .catch(catcher),
);

const NpsReport = lazy(() =>
  import('./pages/NpsReport').then((m) => ({ default: m.NpsReport })).catch(catcher),
);

// Shared error handler
const catcher = (e: Error) => {
  console.log(e);
  return { default: NewVersionAvailable };
};
